import { create } from 'zustand'
import api from '../utils/api.js'
import { logError } from '../utils/logError.js'

const STORAGE_KEY = 'sw-site-filter'
const RECENTS_KEY = 'sw-site-recents'
const DEPT_LENS_KEY = 'sw-site-dept-lens'
const SCHEMA_VERSION = 1
const MAX_RECENTS = 5

function safeGet(key: string): string | null {
  try { return localStorage.getItem(key) } catch { return null }
}
function safeSet(key: string, val: string): void {
  try { localStorage.setItem(key, val) } catch { /* noop */ }
}
function safeRemove(key: string): void {
  try { localStorage.removeItem(key) } catch { /* noop */ }
}

// Parse stored value → array of ID strings. Handles versioned JSON and legacy comma-string.
function parseStored(raw: string | null | undefined): string[] {
  if (!raw) return []
  try {
    const parsed = JSON.parse(raw) as { _v?: number; ids?: unknown[] } | null
    if (parsed && parsed._v === SCHEMA_VERSION && Array.isArray(parsed.ids)) {
      return parsed.ids.map(String).filter(Boolean)
    }
  } catch { /* fall through to legacy */ }
  return String(raw).split(',').map(s => s.trim()).filter(Boolean)
}

function writeStored(ids: string[]): void {
  if (ids.length) {
    safeSet(STORAGE_KEY, JSON.stringify({ _v: SCHEMA_VERSION, ids }))
  } else {
    safeRemove(STORAGE_KEY)
  }
}

// ── Recents localStorage helpers ─────────────────────────────
function parseRecents(raw: string | null | undefined): string[] {
  if (!raw) return []
  try {
    const parsed = JSON.parse(raw) as unknown
    if (Array.isArray(parsed)) return parsed.map(String).filter(Boolean)
  } catch { /* fall through */ }
  return []
}

// ── Debounce + retry for API persistence ─────────────────────
let _debounceTimer: ReturnType<typeof setTimeout> | null = null
let _recentsDebounceTimer: ReturnType<typeof setTimeout> | null = null

async function persistWithRetry(val: string | null, maxRetries = 3): Promise<void> {
  let delay = 500
  let lastErr: unknown
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      await api.put('/users/me/ui-preferences', { site_filter: val })
      return
    } catch (err) {
      lastErr = err
      if (attempt < maxRetries) {
        await new Promise(r => setTimeout(r, delay))
        delay *= 2
      }
    }
  }
  logError('siteFilter.persistFilter', lastErr)
}

function debouncedPersist(val: string | null): void {
  if (_debounceTimer) clearTimeout(_debounceTimer)
  _debounceTimer = setTimeout(() => {
    _debounceTimer = null
    persistWithRetry(val)
  }, 300)
}

function debouncedPersistRecents(recents: string[]): void {
  if (_recentsDebounceTimer) clearTimeout(_recentsDebounceTimer)
  _recentsDebounceTimer = setTimeout(() => {
    _recentsDebounceTimer = null
    api.put('/users/me/ui-preferences', { site_recents: recents }).catch(err => {
      logError('siteFilter.persistRecents', err)
    })
  }, 300)
}

const initial = parseStored(safeGet(STORAGE_KEY))
const initialRecents = parseRecents(safeGet(RECENTS_KEY))
const initialDeptLens = (() => {
  const raw = safeGet(DEPT_LENS_KEY)
  if (!raw || raw === 'all') return null
  return String(raw).trim() || null
})()

/** An identifier accepted by the setters — coerced to a string id internally. */
type SiteIdInput = string | number | null | undefined

export interface SiteFilterState {
  /** Active site filter as normalized string ids. */
  siteIds: string[]
  /** The string ids joined by `,`, or null when the filter is empty. */
  siteId: string | null
  /** Recently-used site ids (most recent first, capped). */
  recents: string[]
  /** Site-setup department lens; null = all departments. */
  deptLens: string | null
  setDeptLens: (deptId: SiteIdInput) => void
  setSiteIds: (ids: ReadonlyArray<SiteIdInput> | string | null | undefined) => void
  setSiteId: (id: SiteIdInput) => void
  toggleSite: (id: string | number) => void
  clearFilter: () => void
  trackRecent: (siteId: string | number) => void
  setRecents: (ids: ReadonlyArray<SiteIdInput> | null | undefined) => void
  /** Remove ids not in validIds. Empty validIds is a no-op. Returns count removed. */
  pruneIds: (validIds: ReadonlyArray<string | number> | null | undefined) => number
}

export const useSiteFilterStore = create<SiteFilterState>((set, get) => ({
  siteIds: initial,
  siteId: initial.length ? initial.join(',') : null,
  recents: initialRecents,
  // Wave 2 — site setup department lens. null = all departments.
  // localStorage-persisted (key: sw-site-dept-lens). No API persistence yet.
  deptLens: initialDeptLens,
  setDeptLens: (deptId) => {
    const val = deptId == null || deptId === '' || deptId === 'all' ? null : String(deptId)
    if (val) safeSet(DEPT_LENS_KEY, val)
    else safeRemove(DEPT_LENS_KEY)
    set({ deptLens: val })
  },

  setSiteIds: (ids) => {
    const prev = get().siteIds.map(String)
    const arr = Array.isArray(ids)
      ? ids.filter(id => id != null && id !== '').map(String).filter(Boolean)
      : parseStored(String(ids || ''))
    writeStored(arr)
    set({ siteIds: arr, siteId: arr.length ? arr.join(',') : null })
    debouncedPersist(arr.length ? arr.join(',') : null)
    // Track newly added site IDs in recents
    const prevSet = new Set(prev)
    arr.filter(id => !prevSet.has(id)).forEach(id => get().trackRecent(id))
  },

  setSiteId: (id) => {
    if (id) get().setSiteIds([String(id)])
    else get().setSiteIds([])
  },

  toggleSite: (id) => {
    const strId = String(id)
    const current = get().siteIds.map(String)
    const next = current.includes(strId)
      ? current.filter(s => s !== strId)
      : [...current, strId]
    get().setSiteIds(next)
  },

  clearFilter: () => get().setSiteIds([]),

  // Track a recently-used site (prepend, deduplicate, cap at MAX_RECENTS)
  trackRecent: (siteId) => {
    const strId = String(siteId)
    const current = get().recents.filter(id => id !== strId)
    const next = [strId, ...current].slice(0, MAX_RECENTS)
    safeSet(RECENTS_KEY, JSON.stringify(next))
    set({ recents: next })
    debouncedPersistRecents(next)
  },

  // Hydrate recents from server-side ui_preferences on login
  setRecents: (ids) => {
    const arr = Array.isArray(ids) ? ids.map(String).filter(Boolean) : []
    safeSet(RECENTS_KEY, JSON.stringify(arr))
    set({ recents: arr })
  },

  // Remove IDs not in validIds. Empty validIds is a no-op. Returns count removed.
  pruneIds: (validIds) => {
    if (!validIds || validIds.length === 0) return 0
    const valid = new Set(validIds.map(String))
    const current = get().siteIds
    const kept = current.filter(id => valid.has(String(id)))
    const removed = current.length - kept.length
    if (removed > 0) {
      writeStored(kept)
      set({ siteIds: kept, siteId: kept.length ? kept.join(',') : null })
      debouncedPersist(kept.length ? kept.join(',') : null)
    }
    return removed
  },
}))

export function reconcileWithSites(sites: ReadonlyArray<{ id: string | number }> | null | undefined): void {
  if (!sites) return
  useSiteFilterStore.getState().pruneIds(sites.map(s => s.id))
}
